import _ from 'lodash';
import { formatBrazilianNumericDecimal } from '../../hooks/form/field/formatters';
import { isStrictEquals, padLeadingZeros, unnormalizeNumeral } from '../general-utils';

const STATUS_SOLICITADO = 'SOLICITADO';
const STATUS_CANCELADO = 'CANCELADO';
const STATUS_CALCULADO = 'CALCULADO';
const STATUS_FATURADO = 'FATURADO';
const STATUS_AGUARDANDO_VALIDACAO_NOTA = 'AGUARDANDO_VALIDACAO_NOTA';
const STATUS_NFSE_EMITIDA = 'NFSE_EMITIDA';
const STATUS_NFSE_REJEITADA = 'NFSE_REJEITADA';
const STATUS_NFSE_CANCELADA = 'NFSE_CANCELADA';

const isStatusSolicitado = (key) => isStrictEquals(key, STATUS_SOLICITADO);

const isStatusCancelado = (key) => isStrictEquals(key, STATUS_CANCELADO);

const isStatusCalculado = (key) => isStrictEquals(key, STATUS_CALCULADO);

const isStatusFaturado = (key) => isStrictEquals(key, STATUS_FATURADO) || isStrictEquals(key, STATUS_NFSE_CANCELADA);

const isStatusAguardandoValidacaoNota = (key) => isStrictEquals(key, STATUS_AGUARDANDO_VALIDACAO_NOTA);

const isStatusNfseEmitida = (key) => isStrictEquals(key, STATUS_NFSE_EMITIDA);

const isStatusNfseRejeitada = (key) => isStrictEquals(key, STATUS_NFSE_REJEITADA);

const getQuantidadeFrom = (values) => _.get(values, 'quantidade') || 0;

const getTotalMoedaFrom = (values) => {
  const totalMoeda = _.get(values, 'totalMoeda');
  const formatted = unnormalizeNumeral(totalMoeda, formatBrazilianNumericDecimal(2));

  return formatted;
};

const getTotalRealFrom = (values) => {
  const totalReal = _.get(values, 'totalReal');
  const formatted = unnormalizeNumeral(totalReal, formatBrazilianNumericDecimal(2));

  return formatted;
};

const formatProcessosCapas = (capas, max) => {
  const quantidadeCapas = capas.length;
  const capasExistentes = quantidadeCapas > 0;

  if (capasExistentes) {
    const mapProcessoCapas = capas
      .map((capa) => capa?.followUp?.numero)
      .slice(0, max && max > 0 ? max : quantidadeCapas);

    const formatProcessosCapasString = mapProcessoCapas.join(' | ');

    return quantidadeCapas > max ? `${formatProcessosCapasString}...` : formatProcessosCapasString;
  }

  return undefined;
};

const formatProcessosCapasReduzido = (capas, max) => {
  const quantidadeCapas = capas.length;
  const capasExistentes = quantidadeCapas > 0;

  if (capasExistentes) {
    const mapProcessoCapas = capas.map((capa) => capa?.numeroProcesso).slice(0, max && max > 0 ? max : quantidadeCapas);

    const formatProcessosCapasString = mapProcessoCapas.join(' | ');

    return quantidadeCapas > max ? `${formatProcessosCapasString}...` : formatProcessosCapasString;
  }

  return undefined;
};

const ORIGEM_PAGO_RECEBIDO_NUMERARIO = 'NUMERARIO';
const ORIGEM_PAGO_RECEBIDO_TRANSFERENCIA_SALDO_ENTRADA = 'TRANSFERENCIA_SALDO_ENTRADA';
const ORIGEM_PAGO_RECEBIDO_CONCILIACAO = 'CONCILIACAO';
const ORIGEM_PAGO_RECEBIDO_DI_DUIMP = 'DI_DUIMP';

const ORIGENS_PAGO_RECEBIDO_CREDITO = [
  ORIGEM_PAGO_RECEBIDO_NUMERARIO,
  ORIGEM_PAGO_RECEBIDO_TRANSFERENCIA_SALDO_ENTRADA,
  ORIGEM_PAGO_RECEBIDO_CONCILIACAO,
];

/**
 * Lançamentos que entram como crédito do processo: numerários, transferências de saldo de entrada
 * e os créditos lançados direto pela Conciliação.
 */
const isOrigemCredito = (origem) => ORIGENS_PAGO_RECEBIDO_CREDITO.includes(origem);

/**
 * Impostos vindos dos pagamentos da DI/DUIMP: o totalMoeda deles é o imposto convertido para a
 * moeda estrangeira da fatura, então a coluna em moeda repete o valor em reais.
 */
const isOrigemDiDuimp = (origem) => isStrictEquals(origem, ORIGEM_PAGO_RECEBIDO_DI_DUIMP);

/**
 * Numerário sempre se apresenta como "Numerário"; crédito vindo da conciliação mostra a despesa
 * informada no lançamento.
 */
const descricaoCredito = (pagoRecebido) => {
  const origem = _.get(pagoRecebido, 'origem');

  if (isStrictEquals(origem, ORIGEM_PAGO_RECEBIDO_NUMERARIO)) {
    return 'Numerário';
  }

  return (
    _.get(pagoRecebido, 'despesaReceita.description') ||
    _.get(pagoRecebido, 'titulo.description') ||
    (origem ? origem.replace(/_/g, ' ') : '-')
  );
};

const formatIdentificacao = (identificacao) => {
  if (!(identificacao?.sequencial || identificacao?.ano)) {
    return undefined;
  }

  const { sequencial, ano } = identificacao;

  const sequencialWithLeadingZeros = padLeadingZeros(sequencial, 4);
  const formattedIdentificacao = `${sequencialWithLeadingZeros}/${ano}`;

  return formattedIdentificacao;
};

const FaturamentoUtils = Object.freeze({
  SOLICITADO: STATUS_SOLICITADO,
  CANCELADO: STATUS_CANCELADO,
  FATURADO: STATUS_FATURADO,
  NOTA_FISCAL_EMITIDA: STATUS_AGUARDANDO_VALIDACAO_NOTA,
  NFSE_EMITIDA: STATUS_NFSE_EMITIDA,
  isSolicitado: isStatusSolicitado,
  isCancelado: isStatusCancelado,
  isCalculado: isStatusCalculado,
  isFaturado: isStatusFaturado,
  isAguardandoValidacaoNota: isStatusAguardandoValidacaoNota,
  isStatusNfseEmitida,
  isStatusNfseRejeitada,
  ORIGEM_NUMERARIO: ORIGEM_PAGO_RECEBIDO_NUMERARIO,
  ORIGEM_CONCILIACAO: ORIGEM_PAGO_RECEBIDO_CONCILIACAO,
  ORIGEM_DI_DUIMP: ORIGEM_PAGO_RECEBIDO_DI_DUIMP,
  isOrigemCredito,
  isOrigemDiDuimp,
  descricaoCredito,
  formatProcessosCapas,
  formatProcessosCapasReduzido,
  formatIdentificacao,
  getQuantidadeFrom,
  getTotalRealFrom,
  getTotalMoedaFrom,
});

export default FaturamentoUtils;
